import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import isString from 'lodash-es/isString';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { copyToClipboard } from '@panneau/core/utils';
import Button from '@panneau/element-button';
import Icon from '@panneau/element-icon';
import TextField from '@panneau/field-text';

var _excluded = ["name", "value", "schemes", "url", "disabled", "prepend", "append", "preview", "copy", "className", "onChange"];
var getScheme = function getScheme(url, schemesPattern) {
  var match = url !== null && isString(url) ? url.match(schemesPattern) : null;
  return match !== null && match[1].length !== url.length ? match[1].toLowerCase() : null;
};
var removeScheme = function removeScheme(url, schemesPattern) {
  return url !== null && isString(url) ? url.replace(schemesPattern, '') : null;
};
var withScheme = function withScheme(url, prefix, schemesPattern) {
  return url !== null && isString(url) && !url.match(schemesPattern) ? "".concat(prefix).concat(url) : url;
};
var propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  schemes: PropTypes.arrayOf(PropTypes.string),
  url: PropTypes.string,
  disabled: PropTypes.bool,
  prepend: PropTypes.string,
  append: PropTypes.string,
  preview: PropTypes.bool,
  copy: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func
};
var defaultProps = {
  name: null,
  value: null,
  schemes: ['https://', 'http://'],
  url: null,
  disabled: null,
  prepend: null,
  append: null,
  preview: false,
  copy: false,
  className: null,
  onChange: null
};
var UrlField = function UrlField(_ref) {
  var name = _ref.name,
    value = _ref.value,
    schemes = _ref.schemes,
    url = _ref.url,
    disabled = _ref.disabled,
    prepend = _ref.prepend,
    append = _ref.append,
    preview = _ref.preview,
    copy = _ref.copy,
    className = _ref.className,
    onChange = _ref.onChange,
    props = _objectWithoutProperties(_ref, _excluded);
  var empty = isEmpty(value);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];
  useEffect(function () {
    setOpen(false);
  }, [value, disabled]);
  var schemesPattern = useMemo(function () {
    return new RegExp("^(".concat(schemes.join('|'), ")"), 'i');
  }, [schemes]);
  var scheme = useMemo(function () {
    return getScheme(value, schemesPattern) || schemes[0];
  }, [value, schemes, schemesPattern]);
  var valueWithoutScheme = useMemo(function () {
    return removeScheme(value, schemesPattern);
  }, [value, schemesPattern]);
  var onFieldChange = useCallback(function (newValue) {
    var newValueWithScheme = !isEmpty(newValue) ? withScheme(newValue, scheme, schemesPattern) : null;
    if (onChange !== null) {
      onChange(newValueWithScheme);
    }
  }, [onChange, scheme, schemesPattern]);
  var onClickOpen = useCallback(function () {
    setOpen(!open);
  }, [open, setOpen]);
  var onClickScheme = useCallback(function (sch) {
    setOpen(false);
    var newValueWithScheme = !isEmpty(value) ? withScheme(valueWithoutScheme, sch, schemesPattern) : null;
    if (onChange !== null) {
      onChange(newValueWithScheme);
    }
  }, [open, setOpen, schemesPattern, valueWithoutScheme, onChange]);
  var onClickCopy = useCallback(function () {
    copyToClipboard(valueWithoutScheme);
  }, [valueWithoutScheme]);
  var finalPrependValue = prepend || url || scheme;
  var finalPrepend = prepend === null && url === null ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    className: classNames(['btn', 'btn-outline-secondary', {
      show: open,
      disabled: disabled || empty,
      'dropdown-toggle': !disabled && !empty,
      'btn-secondary': disabled || empty
    }]),
    type: "button",
    "aria-expanded": open ? 'false' : 'true',
    onClick: !disabled && !empty ? onClickOpen : null,
    disabled: disabled
  }, finalPrependValue), /*#__PURE__*/React.createElement("ul", {
    className: classNames(['dropdown-menu', {
      show: open
    }])
  }, schemes.map(function (sch) {
    return /*#__PURE__*/React.createElement("li", {
      key: "".concat(name, "-").concat(sch)
    }, /*#__PURE__*/React.createElement("button", {
      className: classNames(['dropdown-item', {
        active: sch === scheme
      }]),
      type: "button",
      onClick: function onClick() {
        return onClickScheme(sch);
      },
      disabled: disabled
    }, sch));
  }))) : prepend || url;
  var partialAppend = preview ?
  /*#__PURE__*/
  // eslint-disable-next-line jsx-a11y/control-has-associated-label
  React.createElement("a", {
    href: value !== null ? "".concat(finalPrepend).concat(valueWithoutScheme) : null,
    className: "input-group-text",
    target: "_blank",
    rel: "noopener noreferrer"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "eye-fill"
  })) : append;
  var finalAppend = copy ? /*#__PURE__*/React.createElement("div", {
    className: "input-group-text p-0"
  }, /*#__PURE__*/React.createElement(Button, {
    size: "sm",
    onClick: onClickCopy
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "clipboard"
  }))) : partialAppend;
  return /*#__PURE__*/React.createElement(TextField, Object.assign({}, props, {
    className: className,
    value: valueWithoutScheme,
    onChange: onFieldChange,
    prepend: finalPrepend,
    append: finalAppend,
    disabled: disabled,
    type: "text"
  }));
};
UrlField.propTypes = propTypes;
UrlField.defaultProps = defaultProps;

var definition = {
  id: 'url',
  component: 'Url'
};

export { UrlField as default, definition };
